import { useState } from 'react';

interface ExperienceItem {
  title: string;
  company: string;
  location: string;
  period: string;
  description: string[];
  skills: string[];
  logo: string;
}

const Experience = () => {
  const [expandedBullets, setExpandedBullets] = useState<Set<number>>(new Set());
  const [expandedSkills, setExpandedSkills] = useState<Set<number>>(new Set());

  const experiences: ExperienceItem[] = [
    {
      title: 'AI Engineer',
      company: 'Deriv',
      location: 'Dubai, UAE',
      period: 'Aug \'26 - Present',
      logo: '/images/logos/deriv-logo.svg',
      description: [
        'After my internship I joined Deriv full time as an AI Engineer on the Security & AI team. My job is to find work that the compliance, risk and legal teams are doing by hand and build AI tools that take it off their plate.',
        'I\'m currently building an AI research tool that pulls together information on different markets and turns it into a structured risk assessment. It\'s already being used by VP-level people across teams like Global Partnerships and Content & Media.',
        'I own these tools completely, from the first conversation with the team to running them in production on Google Cloud.',
        'A lot of what I do is general AI engineering: picking the right model for each step, combining several models in one pipeline, and testing prompts against real data until the output is something people can actually trust.',
        'The compliance monitoring system I built as an intern is now part of how the compliance team works every week, and it checks far more partner content than they could ever get through manually.'
      ],
      skills: [
        'Vertex AI',
        'Gemini',
        'LiteLLM',
        'Deep Research Pipelines',
        'Async Python',
        'FastAPI',
        'Next.js',
        'PostgreSQL',
        'Cloud SQL',
        'Cloud Storage',
        'GKE',
        'Kubernetes CronJobs',
        'Cloud Build & Cloud Deploy',
        'Secret Manager',
        'Human-in-the-Loop AI'
      ]
    },
    {
      title: 'AI Engineer Intern',
      company: 'Deriv',
      location: 'Dubai, UAE',
      period: 'Jan \'26 - Jul \'26',
      logo: '/images/logos/deriv-logo.svg',
      description: [
        'Joined Deriv\'s Security & AI team as an intern and ended up being the only engineer on a few internal AI tools that went to production.',
        'Built a compliance monitoring system that goes through partner marketing content every week and flags anything that breaks regulatory guidelines. Before this, the team was reviewing content manually and could only get through a small part of it.',
        'Built a Slack bot that reads engineering channels every day and checks in with system owners once a month, so the risk team can tell when a system\'s risk level needs to change. There was no process for this before.',
        'Learnt how to deploy and run things on Google Cloud: GKE, Cloud Build, Cloud Deploy, Secret Manager and Cloud SQL.',
        'Spent a lot of time on prompts and testing, since a wrong classification in a regulated company can actually cause problems.',
        'Worked closely with the compliance, risk and legal teams to figure out what they needed and build it.'
      ],
      skills: [
        'Google Cloud Platform',
        'GKE',
        'Cloud Build & Cloud Deploy',
        'Secret Manager',
        'Cloud SQL',
        'AI Pipeline Design',
        'Prompt Engineering',
        'Model Orchestration',
        'FastAPI',
        'Next.js',
        'PostgreSQL',
        'Kubernetes',
        'Slack Bot Development',
        'Financial Compliance',
        'Full-Stack Development'
      ]
    },
    {
      title: 'AI/ML Engineering Intern',
      company: 'Bditto',
      location: 'London, England - Remote',
      period: 'July \'25 - Jan \'26',
      logo: '/images/logos/bditto-logo.svg',
      description: [
        'Designed a RAG-based agent framework with a 10-stage pipeline, which covered processing user requests, the API gateway and orchestrating the agents.',
        'Built agents for specific domains, like one for financial documents that used banking logic from Apache Fineract, and OCR pipelines for messy, unstructured data.',
        'Worked on search and reranking (hybrid search, combining results from different sources, merging outputs with an LLM), and used reinforcement learning to improve prompts over time.',
        'Added guardrails like fact checking, bias detection and hallucination checks.',
        'Also worked on the MLOps side with MLflow, LangGraph, and Prometheus + Grafana.'
      ],
      skills: [
        'Retrieval Augmented Generation (RAG)',
        'AI Agents',
        'AI Tool Calling',
        'LangGraph',
        'LLM Optimization',
        'Reinforcement Learning',
        'Apache Fineract',
        'OCR',
        'MLflow',
        'Prometheus'
      ]
    },
    {
      title: 'Technical Executive: AI/ML Head',
      company: 'Google Developer Groups',
      location: 'BITS Pilani',
      period: 'July \'25 - Present',
      logo: '/images/logos/gdg-logo.svg',
      description: [
        'I\'m the AI/ML Head for GDG at BITS Pilani Dubai, where I run workshops, talks and hackathons on AI/ML and Google Cloud.',
        'I also do one-on-one sessions and help students when they\'re stuck on their AI/ML projects.',
        'The goal is to build a community of students on campus who are into AI and Google tech.'
      ],
      skills: [
        'Community Leadership',
        'AI/ML',
        'Workshop Planning',
        'Technical Mentoring',
        'Google Cloud Platform',
        'Event Management',
        'Public Speaking',
        'Team Management'
      ]
    },
    {
      title: 'IT Consultant Intern',
      company: 'Flamingus Technologies',
      location: 'Dubai, UAE',
      period: 'June \'24 - August \'24',
      logo: '/images/logos/flamingus-logo.jpg',
      description: [
        'Got hands-on with cybersecurity tools and how companies actually set them up.',
        'Did cybersecurity and sales certification training, which helped me understand the main security frameworks and products.',
        'Helped build an app from scratch and worked on some web development and IT consulting projects.',
        'Suggested a few technical improvements to the CEO, which ended up being implemented.',
        'Worked with Microsoft 365, VMware, Acronis and Veeam, mostly around virtualization, endpoint security and cloud services.',
        'Gave daily presentations on different tech topics and wrote weekly summaries of my work.'
      ],
      skills: [
        'Cybersecurity',
        'Cloud Computing',
        'Virtualization',
        'Web Design',
        'Project Management'
      ]
    }
  ];

  const toggleBullets = (index: number) => {
    setExpandedBullets(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleSkills = (index: number) => {
    setExpandedSkills(prev => {
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  };

  // Group consecutive entries at the same company into one card (the two
  // Deriv roles), rather than repeating the company header per role.
  type Group = { company: string; location: string; logo: string; roles: { item: ExperienceItem; index: number }[] };
  const groups: Group[] = [];
  experiences.forEach((item, index) => {
    const last = groups[groups.length - 1];
    if (last && last.company === item.company) {
      last.roles.push({ item, index });
    } else {
      groups.push({ company: item.company, location: item.location, logo: item.logo, roles: [{ item, index }] });
    }
  });

  // Entries are listed newest-first, so within a group the first role is the
  // most recent and the last role is the oldest. The header's combined span
  // is built from those two period strings as written, no date parsing.
  const groupDateRange = (roles: Group['roles']) => {
    const [, latestEnd] = roles[0].item.period.split(' - ');
    const [earliestStart] = roles[roles.length - 1].item.period.split(' - ');
    return `${earliestStart} - ${latestEnd}`;
  };

  return (
    <div className="space-y-5">
      {groups.map((group, groupIndex) => (
        <div key={groupIndex} className="dev-card p-5 md:p-6">
          {/* Company header */}
          <div className="flex items-start justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-white rounded-md p-1.5 border border-dev-border/30">
                <img
                  src={group.logo}
                  alt={`${group.company} logo`}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div>
                <div className="font-mono font-semibold text-dev-text text-lg">{group.company}</div>
                <div className="text-dev-text-dimmed text-xs">{group.location}</div>
              </div>
            </div>
            <div className="text-dev-text-dimmed text-xs font-mono shrink-0 mt-1">
              {groupDateRange(group.roles)}
            </div>
          </div>

          {/* Roles within this company */}
          <div className={group.roles.length > 1 ? 'relative pl-5' : ''}>
            {group.roles.length > 1 && (
              <div className="absolute left-0 top-1 bottom-1 w-px bg-dev-border" />
            )}

            <div className="space-y-5">
              {group.roles.map(({ item, index }, roleIdx) => {
                const bulletsExpanded = expandedBullets.has(index);
                const skillsExpanded = expandedSkills.has(index);
                const bulletsToShow = bulletsExpanded ? item.description : item.description.slice(0, 2);
                const skillsToShow = skillsExpanded ? item.skills : item.skills.slice(0, 6);
                const hiddenSkillsCount = item.skills.length - 6;

                return (
                  <div key={index} className="relative">
                    {group.roles.length > 1 && (
                      <span
                        className={`absolute -left-6 top-1.5 w-2 h-2 rounded-full ${
                          roleIdx === 0 ? 'bg-dev-accent' : 'border border-dev-text-dimmed bg-dev-bg-card'
                        }`}
                      />
                    )}

                    <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-1 md:gap-3">
                      <h3 className="font-mono font-semibold text-dev-text text-base md:text-lg">
                        {item.title}
                      </h3>
                      <div className="text-dev-text-dimmed text-xs font-mono shrink-0 md:text-right">
                        {item.period}
                      </div>
                    </div>

                    {item.description.length === 1 ? (
                      <p className="mt-2 text-dev-text-muted text-sm leading-relaxed">
                        {item.description[0]}
                      </p>
                    ) : (
                      <ul className="mt-2 list-disc list-outside ml-4 space-y-1.5 text-dev-text-muted text-sm">
                        {bulletsToShow.map((desc, i) => (
                          <li key={i} className="leading-relaxed">{desc}</li>
                        ))}
                      </ul>
                    )}

                    {item.description.length > 2 && (
                      <button
                        onClick={() => toggleBullets(index)}
                        className="mt-1.5 text-dev-accent text-xs font-mono hover:underline"
                      >
                        {bulletsExpanded ? 'Show less' : `Show all (${item.description.length})`}
                      </button>
                    )}

                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {skillsToShow.map((skill, i) => (
                        <span
                          key={i}
                          className="text-[11px] font-mono px-2 py-0.5 rounded border border-dev-border text-dev-text-dimmed"
                        >
                          {skill}
                        </span>
                      ))}
                      {!skillsExpanded && hiddenSkillsCount > 0 && (
                        <button
                          onClick={() => toggleSkills(index)}
                          className="text-[11px] font-mono px-2 py-0.5 rounded border border-dev-border text-dev-text-dimmed hover:text-dev-accent hover:border-dev-accent transition-colors"
                        >
                          +{hiddenSkillsCount}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default Experience;
